import React, { useState, useRef } from 'react';
import { professorsData } from '../data/mock';
import { Menu, X, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import { useLang } from '../i18n/LanguageContext';
import { useT } from '../i18n/useT';

const CampusDropdown = ({ isDark }) => {
  const t = useT();
  const items = t.campuses.items;
  return (
    <div
      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 py-3 rounded-xl min-w-[240px]"
      style={{
        backgroundColor: isDark ? 'rgba(7, 7, 15, 0.95)' : 'rgba(255,255,255,0.98)',
        border: isDark ? '1px solid rgba(0,212,255,0.12)' : '1px solid rgba(27,42,74,0.1)',
        backdropFilter: 'blur(16px)',
        boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.6)' : '0 8px 32px rgba(0,0,0,0.12)',
      }}
    >
      {items.map((f, i) => (
        <a
          key={i}
          href="#professions"
          className="block px-5 py-2.5 text-[14px] transition-all duration-200"
          style={{
            fontFamily: '"Space Grotesk", sans-serif',
            color: isDark ? 'rgba(255,255,255,0.7)' : '#1B2A4A',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = isDark ? 'rgba(0,212,255,0.08)' : 'rgba(27,42,74,0.05)';
            e.target.style.color = isDark ? '#E6EBF2' : '#1B2A4A';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = isDark ? 'rgba(255,255,255,0.7)' : '#1B2A4A';
          }}
        >
          {f.name}
        </a>
      ))}
    </div>
  );
};

const CampusOverlay = ({ onClose, isDark }) => {
  const t = useT();
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="campus-overlay">
      <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }} onClick={onClose} />
      <div className="relative z-10 w-full max-w-[1200px] mx-4 max-h-[85vh] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-[28px] lg:text-[36px] font-bold text-white" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{t.professors.title}</h2>
            <p className="text-[14px] mt-1 flex items-center gap-2" style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.5)' }}>
              <Lock size={13} />
              {t.professors.subtitle}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full transition-all hover:scale-110" style={{ color: '#E6EBF2' }}>
            <X size={28} />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {professorsData.map((prof, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
              style={{ backgroundColor: 'rgba(7, 7, 15, 0.9)', border: '1px solid rgba(0, 212, 255, 0.1)' }}
            >
              <div className="relative h-[200px] overflow-hidden">
                <img src={prof.image} alt="Agent" className="w-full h-full object-cover" style={{ filter: 'blur(14px) brightness(0.6)', transform: 'scale(1.2)' }} />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 30%, rgba(7,7,15,0.95) 100%)' }} />
                <div className="absolute bottom-4 left-5 right-5">
                  <p className="text-[11px] uppercase tracking-widest mb-1" style={{ fontFamily: '"Inter", sans-serif', color: '#E6EBF2' }}>
                    {prof.campus}
                  </p>
                  <h3 className="text-[20px] font-bold text-white flex items-center gap-2" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                    {prof.codename}
                    <Lock size={14} className="opacity-40" />
                  </h3>
                </div>
              </div>
              <div className="p-5 pt-3">
                <div className="space-y-2.5">
                  {prof.achievements.map((a, j) => (
                    <div key={j} className="flex items-center gap-3">
                      <div className="w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ backgroundColor: '#E6EBF2', boxShadow: '0 0 6px rgba(0,212,255,0.5)' }} />
                      <p className="text-[13px]" style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.7)' }}>{a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const LanguageToggle = ({ isDark }) => {
  const { lang, toggleLang } = useLang();
  return (
    <button
      onClick={toggleLang}
      data-testid="language-toggle"
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-bold transition-all duration-200 hover:scale-105"
      style={{
        backgroundColor: isDark ? 'rgba(0,212,255,0.08)' : 'rgba(27,42,74,0.06)',
        border: isDark ? '1px solid rgba(0,212,255,0.15)' : '1px solid rgba(27,42,74,0.1)',
        fontFamily: '"Inter", sans-serif',
        color: isDark ? '#E6EBF2' : '#1B2A4A',
      }}
      title={lang === 'fr' ? 'Switch to English' : 'Passer en Francais'}
    >
      <span className="text-[16px]">{lang === 'fr' ? '\uD83C\uDDEB\uD83C\uDDF7' : '\uD83C\uDDFA\uD83C\uDDF8'}</span>
      <span>{lang === 'fr' ? 'FR' : 'EN'}</span>
    </button>
  );
};

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [campusOpen, setCampusOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownTimeout = useRef(null);
  const { isDark } = useTheme();
  const t = useT();

  const navLinks = [
    { label: t.nav.home, href: '#' },
    { label: t.nav.courses, href: '#professions' },
    { label: t.nav.testimonials, href: '#reviews' },
    { label: t.nav.method, href: '#information' },
    { label: t.nav.campus, href: '#campus', overlay: true },
  ];

  const handleDropdownEnter = () => {
    clearTimeout(dropdownTimeout.current);
    setDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => setDropdownOpen(false), 200);
  };

  return (
    <>
      <nav className="relative z-50 w-full">
        <div className="mx-auto max-w-[1140px] px-4">
          <div className="flex items-center justify-between py-4">
            <a href="#" className="flex items-center gap-2 text-white no-underline">
              <span className="text-[22px] font-extrabold tracking-tight" style={{ fontFamily: '"Inter", sans-serif' }}>OpenBro</span>
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" aria-hidden="true"><path fill="#fff" d="M12 0c.6 7.2 4.8 11.4 12 12-7.2.6-11.4 4.8-12 12-.6-7.2-4.8-11.4-12-12 7.2-.6 11.4-4.8 12-12z" /></svg>
            </a>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) =>
                link.overlay ? (
                  <div
                    key={link.label}
                    className="relative"
                    onMouseEnter={handleDropdownEnter}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <button
                      onClick={() => { setCampusOpen(true); setDropdownOpen(false); }}
                      className="text-[15px] font-medium transition-all duration-300 cursor-pointer bg-transparent border-none"
                      style={{ fontFamily: '"Inter", sans-serif', color: isDark ? 'rgba(255,255,255,0.7)' : '#1B2A4A' }}
                      onMouseEnter={(e) => { if (isDark) e.target.style.color = '#E6EBF2'; }}
                      onMouseLeave={(e) => { if (isDark) e.target.style.color = 'rgba(255,255,255,0.7)'; }}
                    >
                      {link.label}
                    </button>
                    {dropdownOpen && <CampusDropdown isDark={isDark} />}
                  </div>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-[15px] font-medium transition-all duration-300"
                    style={{ fontFamily: '"Inter", sans-serif', color: isDark ? 'rgba(255,255,255,0.7)' : '#1B2A4A' }}
                    onMouseEnter={(e) => { if (isDark) e.target.style.color = '#E6EBF2'; }}
                    onMouseLeave={(e) => { if (isDark) e.target.style.color = 'rgba(255,255,255,0.7)'; }}
                  >
                    {link.label}
                  </a>
                )
              )}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <LanguageToggle isDark={isDark} />
              <Link to="/login">
                <button className={`px-6 py-2.5 text-[15px] font-bold border-2 transition-all duration-300 ${isDark ? 'tech-glow' : ''}`} style={{ backgroundColor: isDark ? 'transparent' : '#1B2A4A', color: isDark ? '#E6EBF2' : 'white', borderColor: isDark ? '#E6EBF2' : '#1B2A4A' }}>{t.nav.login}</button>
              </Link>
            </div>

            <div className="flex lg:hidden items-center gap-2">
              <LanguageToggle isDark={isDark} />
              <button style={{ color: isDark ? '#FFFFFF' : '#1B2A4A' }} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className={`lg:hidden border-t pb-4 ${isDark ? 'tech-card' : ''}`} style={{ backgroundColor: isDark ? '#07070f' : 'white', borderColor: isDark ? 'rgba(0,212,255,0.1)' : '#E5E7EB' }}>
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.overlay ? undefined : link.href}
                  className="block py-3 px-4 text-[15px] font-medium cursor-pointer"
                  style={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#1B2A4A' }}
                  onClick={(e) => {
                    if (link.overlay) { e.preventDefault(); setCampusOpen(true); }
                    setMobileMenuOpen(false);
                  }}
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-2 mt-4 px-4">
                <Link to="/login">
                  <button className="w-full px-6 py-2.5 text-[15px] font-bold border-2" style={{ backgroundColor: isDark ? 'transparent' : '#1B2A4A', color: isDark ? '#E6EBF2' : 'white', borderColor: isDark ? '#E6EBF2' : '#1B2A4A' }}>{t.nav.login}</button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {campusOpen && <CampusOverlay onClose={() => setCampusOpen(false)} isDark={isDark} />}
    </>
  );
};

export default Navbar;
